import React, { useState, useRef, useEffect } from 'react';
import './AudioRecorder.css';
import {
  DEFAULT_SILENCE_MS,
  endsWithTrailingConjunction,
  getMaxSilenceExtensionMs,
} from '../../utils/turnDetection';
import { isInterruptionPhrase } from '../../utils/audioEchoCancellation';

const SILENCE_CHECK_INTERVAL_MS = 150;

// Minimum contiguous word-run (present, in order, somewhere in what the AI
// said) required to call a buffered fragment an echo, and the minimum
// fraction of the fragment that run has to cover. Both conditions must
// hold — chosen to survive the exact false-positive case flagged when
// this was proposed: AI's line ending "...over the other" must not flag a
// genuine answer starting "well, all of the other options..." (their only
// real overlap, "the other", is 2 words — under the 4-word floor either
// way). Client-side, cheaper heuristic than speaker_leakage.py's server-
// side difflib version by design (this has to run synchronously on every
// turn-start, no room for a fuzzy/edit-distance pass) — a separate,
// earlier defense layer, not a replacement for that check.
const ECHO_MIN_RUN_WORDS = 4;
const ECHO_MIN_RUN_FRACTION = 0.6;

function _normalizeForEchoCheck(text) {
  return (text || '')
    .toLowerCase()
    .replace(/[^\w\s']/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Does `bufferedText` (captured while the AI was speaking) look like an
 * echo of `spokenText` (everything the AI has said so far), rather than
 * genuine candidate speech? Pure function, no side effects — easy to unit
 * test independent of the recognition/timer machinery around it. */
export function looksLikeAiEcho(bufferedText, spokenText) {
  const buf = _normalizeForEchoCheck(bufferedText);
  const spoken = _normalizeForEchoCheck(spokenText);
  if (!buf || !spoken) {
    return { isEcho: false, reason: 'nothing to compare' };
  }
  const bufWords = buf.split(' ');
  if (bufWords.length < 3) {
    // Too short to tell an echo apart from a genuine terse answer that
    // happens to reuse a word from the question — same reasoning
    // speaker_leakage.py's own min_candidate_tokens guard uses.
    return { isEcho: false, reason: `buffered fragment only ${bufWords.length} word(s) — too short to judge` };
  }

  if (spoken.includes(buf)) {
    return {
      isEcho: true,
      reason: `buffered fragment is a verbatim substring of AI speech (${bufWords.length} words)`,
      runWords: bufWords.length,
      fraction: 1,
    };
  }

  // Fuzzy fallback: longest contiguous run of buffered words that appears,
  // in the same order, anywhere in what the AI said (STT can drift a word
  // or two even within a real echo).
  // Search from every starting position in BOTH texts, not just the front
  // of the buffered fragment — a leaked echo often has junk at its own
  // start (STT mishearing the tail of whatever came immediately before,
  // e.g. "Could you" heard as "do"), so anchoring only at bufWords[0]
  // misses the real run entirely. Real capture that exposed this:
  // buffered "do your answer to..." against spoken "...your answer to...
  // using LangChain..." — the actual 10-word match starts at "your", one
  // word in, which an anchored-at-0 search never finds.
  const spokenWords = spoken.split(' ');
  let bestRun = 0;
  for (let bufStart = 0; bufStart < bufWords.length; bufStart += 1) {
    for (let spokenStart = 0; spokenStart < spokenWords.length; spokenStart += 1) {
      let run = 0;
      while (
        bufStart + run < bufWords.length
        && spokenStart + run < spokenWords.length
        && bufWords[bufStart + run] === spokenWords[spokenStart + run]
      ) {
        run += 1;
      }
      if (run > bestRun) bestRun = run;
    }
  }
  const fraction = bestRun / bufWords.length;
  const isEcho = bestRun >= ECHO_MIN_RUN_WORDS && fraction >= ECHO_MIN_RUN_FRACTION;
  return {
    isEcho,
    reason: `contiguous run ${bestRun}/${bufWords.length} words (${Math.round(fraction * 100)}%) `
      + `against a ${bestRun >= ECHO_MIN_RUN_WORDS ? 'met' : 'unmet'} ${ECHO_MIN_RUN_WORDS}-word floor`,
    runWords: bestRun,
    fraction,
  };
}

const LANG_MAP = {
  en: 'en-US',
  'en-IN': 'en-IN',
  // es-US helps with Spanish + English technical words/code-switching
  es: 'es-US',
  'es-CR': 'es-CR',
};

export const VoiceRecorder = ({
  isRecording,
  onStart,
  onStop,
  onTranscript,
  onAudioBlob,
  onSilenceDetected,
  silenceThresholdMs = DEFAULT_SILENCE_MS,
  disabled,
  interviewCompleted,
  showTranscript = false,
  language = 'en',
  // True while the AI's own TTS (question/acknowledgement/closing) is
  // playing. No barge-in — the AI is never interrupted — but recognition
  // now runs continuously across the whole interview, so anything the
  // candidate says during this window must still be captured, not
  // dropped. It's buffered (preAnswerBufferRef) and folded into the real
  // answer once this flips back to false, instead of being lost the way
  // it was before recognition ran continuously.
  aiSpeaking = false,
  // Every AI clip's text spoken so far this interview (see
  // CandidateInterviewRoom.jsx's lastSpokenTextRef) — compared against a
  // buffered aiSpeaking-window fragment at fold time to tell a genuine
  // early-start answer apart from the AI's own speaker audio leaking into
  // the mic. Optional: absent (or stale), the fold behaves exactly as
  // before this check existed.
  lastSpokenText = '',
  micEnabled = true,
}) => {
  const recordingSecondsRef = useRef(0);
  const [interimTranscript, setInterimTranscript] = useState('');
  const [finalTranscript, setFinalTranscript] = useState('');

  const recognitionRef = useRef(null);
  const timerRef = useRef(null);
  const fullTranscriptRef = useRef('');
  const lastSpokenTextRef = useRef(lastSpokenText);
  useEffect(() => { lastSpokenTextRef.current = lastSpokenText; }, [lastSpokenText]);

  // Step 5 — turn detection: fires onSilenceDetected once sustained silence
  // follows real speech. Never fires on pure silence (nothing said yet) —
  // that's Step 4's SILENCE intent, a separate 30s/60s concern, not this.
  const silenceCheckRef = useRef(null);
  const lastActivityRef = useRef(0);
  const silenceFiredRef = useRef(false);
  // Chrome's continuous-mode SpeechRecognition can go a long time — in
  // real testing, 60+ seconds — without ever promoting interim results to
  // a final one. Gating the silence timer on fullTranscriptRef (final-only)
  // meant it could never fire while waiting on a final that might not come
  // for a long time, even though the candidate had clearly finished
  // speaking. Track the latest interim text too so both the "has anything
  // been said" gate and the eventual submitted text don't depend on the
  // browser ever finalizing.
  const pendingInterimRef = useRef('');
  // Holds finalized speech captured while aiSpeaking is true — never sent
  // to the backend directly (onTranscript is skipped for it), folded into
  // fullTranscriptRef the moment aiSpeaking flips back to false.
  const preAnswerBufferRef = useRef('');
  const aiSpeakingRef = useRef(aiSpeaking);
  // Bug fix (real production repro): auto-submit was reading the transcript
  // and calling onStop() synchronously, in the same tick that triggered
  // recognition.stop() — before the browser had delivered the trailing
  // final result for what was just said. The answer window then closed
  // (Step 5) and CandidateInterviewRoom's stale-chunk guard (Step 4)
  // correctly discarded that late-arriving final text as "not in answer
  // window" — so a real spoken answer reached the server as "". Fix:
  // wait for recognition.onend (fired only after any pending final result
  // has already reached onresult) before computing finalText and calling
  // onStop — capture, THEN close, THEN submit. A safety timeout covers
  // browsers where onend is slow or doesn't reliably follow stop().
  const awaitingFinalizeRef = useRef(false);
  const finalizeTimeoutRef = useRef(null);
  const FINALIZE_TIMEOUT_MS = 1500;

  const finalizeAndReportStop = () => {
    if (finalizeTimeoutRef.current) {
      clearTimeout(finalizeTimeoutRef.current);
      finalizeTimeoutRef.current = null;
    }
    awaitingFinalizeRef.current = false;
    const finalText = (fullTranscriptRef.current + ' ' + pendingInterimRef.current).trim();
    pendingInterimRef.current = '';
    console.log(`📤 Calling onStop with: "${finalText}"`);
    if (onStopRef.current) {
      onStopRef.current(finalText);
    }
  };
  const onSilenceDetectedRef = useRef(onSilenceDetected);
  const silenceThresholdMsRef = useRef(silenceThresholdMs);
  useEffect(() => { onSilenceDetectedRef.current = onSilenceDetected; }, [onSilenceDetected]);
  useEffect(() => { silenceThresholdMsRef.current = silenceThresholdMs; }, [silenceThresholdMs]);

  // MediaRecorder (parallel audio capture for Whisper)
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const whisperStreamRef = useRef(null);

  // ✅ SOLUTION: Track recognition state ourselves
  const isActiveRef = useRef(false); // Track if recognition.start() was called successfully
  const isStoppingRef = useRef(false); // Track if we're intentionally stopping
  const micPausedRef = useRef(false); // Soft-pause STT while answer recording stays open
  const micEnabledRef = useRef(micEnabled);

  const interviewCompletedRef = useRef(interviewCompleted);
  // Keep callback refs always current so the start/stop effect doesn't need them as deps
  const onStopRef = useRef(onStop);
  const onTranscriptRef = useRef(onTranscript);
  const onAudioBlobRef = useRef(onAudioBlob);

  // Keep refs updated
  useEffect(() => {
    interviewCompletedRef.current = interviewCompleted;
  }, [interviewCompleted]);

  useEffect(() => { onStopRef.current = onStop; }, [onStop]);
  useEffect(() => { onTranscriptRef.current = onTranscript; }, [onTranscript]);
  useEffect(() => { onAudioBlobRef.current = onAudioBlob; }, [onAudioBlob]);
  useEffect(() => { micEnabledRef.current = micEnabled; }, [micEnabled]);

  // Initialize recognition ONCE on mount
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      console.error('❌ Browser does not support Speech Recognition API');
      alert('Your browser does not support speech recognition. Please use Chrome, Edge, or Safari.');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = LANG_MAP[language] || 'en-US';
    recognition.maxAlternatives = 1;
    
    recognitionRef.current = recognition;
    console.log('✅ Speech Recognition initialized');

    // ============ EVENT HANDLERS ============
    
    recognition.onstart = () => {
      console.log('🎤 Recognition STARTED - microphone is active!');
      isActiveRef.current = true; // ✅ Mark as active
      
      if (onStart) {
        onStart();
      }
    };
    
    recognition.onresult = (event) => {
      console.log(`📝 onresult fired! Results: ${event.results.length}`);
      lastActivityRef.current = Date.now();

      let interim = '';
      let finalChunk = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        const confidence = event.results[i][0].confidence || 0;

        if (event.results[i].isFinal) {
          const trimmed = transcript.trim();
          if (trimmed) {
            console.log(`✅ FINAL: "${trimmed}" (confidence: ${confidence.toFixed(2)})`);
            finalChunk += trimmed + ' ';
            pendingInterimRef.current = '';

            if (aiSpeakingRef.current) {
              if (isInterruptionPhrase(trimmed)) {
                console.log(`✋ Speech Recognition: Discarding barge-in interruption command while AI speaking: "${trimmed}"`);
              } else {
                // AI is still talking — not interrupted, but this isn't
                // discarded either. Buffered until aiSpeaking flips false.
                console.log(`🗄️ Buffering (AI still speaking): "${trimmed}"`);
                preAnswerBufferRef.current += trimmed + ' ';
              }
            } else {
              fullTranscriptRef.current += trimmed + ' ';

              // Use ref so we always call the latest callback without stale closure
              if (onTranscriptRef.current && !interviewCompletedRef.current) {
                console.log(`📤 Sending to backend: "${trimmed}"`);
                onTranscriptRef.current(trimmed, true);
              }
            }
          }
        } else {
          interim += transcript;
        }
      }

      if (finalChunk) {
        setFinalTranscript(fullTranscriptRef.current);
      }
      if (interim) {
        pendingInterimRef.current = interim;
        setInterimTranscript(interim);
        // Interim results are unreliable as CONTENT — the recognizer
        // revises/replaces them before they finalize — so they are never
        // sent as answer text (only the isFinal branch above does that).
        // But an interim result is proof the candidate is still speaking:
        // forward it as a non-final signal so the room can keep the
        // server's silence clock alive through a long or halting answer.
        // Gated on aiSpeaking like the final path — while the interviewer
        // is still talking it isn't the candidate's turn yet.
        if (
          onTranscriptRef.current &&
          !interviewCompletedRef.current &&
          !aiSpeakingRef.current
        ) {
          onTranscriptRef.current(interim, false);
        }
      }
    };

    recognition.onerror = (event) => {
      console.error('❌ Recognition error:', event.error);
      
      if (event.error === 'no-speech') {
        console.warn('⚠️ No speech detected (silence is normal)');
      } else if (event.error === 'audio-capture') {
        alert('No microphone found. Please check your microphone.');
        isActiveRef.current = false;
      } else if (event.error === 'not-allowed') {
        alert('Microphone permission denied.');
        isActiveRef.current = false;
      } else if (event.error === 'aborted') {
        console.log('ℹ️ Recognition aborted');
        isActiveRef.current = false;
      } else {
        console.error('❌ Unknown error:', event.error);
        isActiveRef.current = false;
      }
    };

    recognition.onend = () => {
      console.log('🛑 Recognition ENDED');
      isActiveRef.current = false; // ✅ Mark as inactive

      // An intentional stop (auto-submit or manual "I'm done") is waiting
      // right here for recognition to actually finish before reporting the
      // transcript — onend only fires after any trailing final onresult
      // has already landed, so this is the correct place to read it.
      if (awaitingFinalizeRef.current) {
        console.log('✅ Recognition ended — finalizing captured transcript');
        finalizeAndReportStop();
        return;
      }

      // Auto-restart only if we didn't intentionally stop / mute-pause
      if (
        !isStoppingRef.current
        && !interviewCompletedRef.current
        && !micPausedRef.current
        && micEnabledRef.current
      ) {
        console.log('🔄 Auto-restarting recognition...');
        
        setTimeout(() => {
          if (
            !isStoppingRef.current
            && !interviewCompletedRef.current
            && !micPausedRef.current
            && micEnabledRef.current
          ) {
            try {
              recognitionRef.current.start();
              console.log('✅ Restarted successfully');
            } catch (e) {
              console.warn('⚠️ Restart failed:', e.message);
            }
          }
        }, 100);
      } else {
        console.log('✅ Recognition ended cleanly (intentional stop or muted)');
      }
    };

    // Cleanup on unmount
    return () => {
      console.log('🧹 Cleaning up recognition');
      isStoppingRef.current = true;
      // A pending finalize must never fire after unmount (e.g. onend
      // landing just after teardown) — that would call onStop on a
      // component that's already gone.
      awaitingFinalizeRef.current = false;
      if (finalizeTimeoutRef.current) {
        clearTimeout(finalizeTimeoutRef.current);
        finalizeTimeoutRef.current = null;
      }
      if (silenceCheckRef.current) {
        clearInterval(silenceCheckRef.current);
        silenceCheckRef.current = null;
      }
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch (e) { /* ignore */ }
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        try { mediaRecorderRef.current.stop(); } catch (e) { /* ignore */ }
        mediaRecorderRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- single SpeechRecognition instance; deps would recreate recognition
  }, []);

  // Keep recognition language in sync with campaign language
  useEffect(() => {
    if (recognitionRef.current) {
      recognitionRef.current.lang = LANG_MAP[language] || 'en-US';
      console.log(`🌐 Recognition language set to: ${recognitionRef.current.lang}`);
    }
  }, [language]);

  // Keep the ref current for onresult's synchronous branch check. The
  // buffered content itself is folded into fullTranscriptRef inside the
  // START RECORDING block below (turn start), not here — doing it there
  // instead of in a separate effect avoids a real ordering race: this
  // effect and the isRecording effect below can both fire off the same
  // "AI just finished talking, arm the next turn" transition, and React
  // runs effects in declaration order, so a separate merge-then-reset
  // across two effects could have the reset silently wipe what was just
  // merged depending on which one happened to run first.
  useEffect(() => {
    aiSpeakingRef.current = aiSpeaking;
  }, [aiSpeaking]);

  // Tracks isRecording as of the last time the effect below ran, so that
  // effect can tell "a real answer turn just ended" (do the full finalize
  // dance) apart from "aiSpeaking merely also turned off with no turn ever
  // active" (nothing to submit) — see the STOP branch below.
  const prevIsRecordingRef = useRef(false);

  // ============ START/STOP CONTROL ============
  // Deps: isRecording, aiSpeaking, interviewCompleted. onStop/onTranscript
  // are accessed via refs so prop identity changes (e.g. handleTranscript
  // recreated by useCallback) don't trigger this effect and silently clear
  // fullTranscriptRef.current mid-answer.
  //
  // Recognition now listens whenever isRecording OR aiSpeaking is true —
  // not just isRecording — so speech during the AI's own TTS
  // (question/acknowledgement/closing) is captured via onresult's
  // aiSpeaking-gated branch (see above) instead of the mic simply being off
  // for that whole window. Turn-scoped state (the fullTranscriptRef reset,
  // the silence timer, the Whisper-backup MediaRecorder) still only starts
  // on a genuine turn (isRecording true), matching prior behavior — the
  // only change there is that a turn's reset now folds in whatever was
  // buffered from the preceding aiSpeaking window instead of discarding it.
  useEffect(() => {
    if (!recognitionRef.current) {
      console.warn('⚠️ Recognition not initialized yet');
      return;
    }

    // Listen strictly when isRecording is true (the candidate's active answering turn).
    // Mic is muted while David is speaking (aiSpeaking) to eliminate echo leakage and phantom speech.
    const shouldListen = isRecording && !interviewCompleted;
    const wasRecordingTurn = prevIsRecordingRef.current;
    prevIsRecordingRef.current = isRecording;
    console.log(`🔍 isRecording=${isRecording} aiSpeaking=${aiSpeaking} shouldListen=${shouldListen} isActive=${isActiveRef.current}`);

    if (shouldListen) {
      if (isRecording) {
        // ===== START RECORDING (a real turn) =====
        console.log('▶️ Starting recording...');

        // Barge-in and AI-speaking buffering unplugged — start every turn with a completely clean slate
        preAnswerBufferRef.current = '';
        fullTranscriptRef.current = '';
        pendingInterimRef.current = '';
        setFinalTranscript('');
        setInterimTranscript('');
        recordingSecondsRef.current = 0;
        isStoppingRef.current = false;
        micPausedRef.current = !micEnabledRef.current;
        awaitingFinalizeRef.current = false;
        if (finalizeTimeoutRef.current) {
          clearTimeout(finalizeTimeoutRef.current);
          finalizeTimeoutRef.current = null;
        }

        // Step 5 — turn detection: poll for sustained silence after real
        // speech. Threshold is read fresh each tick (via the ref) so a
        // localStorage tuning change takes effect on the next question
        // without needing a reload mid-interview.
        lastActivityRef.current = Date.now();
        silenceFiredRef.current = false;
        if (silenceCheckRef.current) clearInterval(silenceCheckRef.current);
        silenceCheckRef.current = setInterval(() => {
          if (silenceFiredRef.current || isStoppingRef.current) return;
          const spoken = (fullTranscriptRef.current + ' ' + pendingInterimRef.current).trim();
          if (!spoken) return; // nothing said yet — not this mechanism's job

          const elapsed = Date.now() - lastActivityRef.current;
          const base = silenceThresholdMsRef.current;
          const threshold = endsWithTrailingConjunction(spoken)
            ? Math.min(base * 2, base + getMaxSilenceExtensionMs())
            : base;

          if (elapsed >= threshold) {
            silenceFiredRef.current = true;
            console.log(`🔇 Silence detected (${elapsed}ms >= ${threshold}ms) — auto-submitting`);
            if (onSilenceDetectedRef.current) onSilenceDetectedRef.current();
          }
        }, SILENCE_CHECK_INTERVAL_MS);

        // Start MediaRecorder in parallel for full-audio Whisper backup
        audioChunksRef.current = [];
        (async () => {
          try {
            const stream = await navigator.mediaDevices.getUserMedia({
              audio: {
                echoCancellation: { ideal: true },
                noiseSuppression: { ideal: true },
                autoGainControl: { ideal: true },
                googEchoCancellation: { ideal: true },
                googAutoGainControl: { ideal: true },
                googNoiseSuppression: { ideal: true },
                googHighpassFilter: { ideal: true },
              },
            });
            if (isStoppingRef.current) { stream.getTracks().forEach(t => t.stop()); return; }
            // Track the stream so the mic-toggle effect can enable/disable
            // its audio tracks when the candidate mutes mid-answer.
            stream.getAudioTracks().forEach((t) => { t.enabled = micEnabledRef.current; });
            whisperStreamRef.current = stream;
            const mimeType = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg'].find(
              t => MediaRecorder.isTypeSupported(t)
            ) || 'audio/webm';
            const recorder = new MediaRecorder(stream, { mimeType });
            recorder.ondataavailable = (e) => {
              if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
            };
            recorder.onstop = () => {
              stream.getTracks().forEach(t => t.stop());
              if (whisperStreamRef.current === stream) whisperStreamRef.current = null;
              if (audioChunksRef.current.length > 0 && onAudioBlobRef.current) {
                const blob = new Blob(audioChunksRef.current, { type: recorder.mimeType || 'audio/webm' });
                console.log(`🎙️ Audio blob ready: ${(blob.size / 1024).toFixed(0)} KB`);
                onAudioBlobRef.current(blob);
              }
            };
            recorder.start(1000);
            mediaRecorderRef.current = recorder;
            console.log('🎙️ MediaRecorder started:', mimeType);
          } catch (e) {
            console.warn('⚠️ MediaRecorder setup failed (STT only):', e.message);
          }
        })();

        if (!timerRef.current) {
          timerRef.current = setInterval(() => {
            recordingSecondsRef.current += 1;
          }, 1000);
        }
      } else {
        // AI is speaking, no turn active yet — just keep recognition warm
        // so onresult can buffer results into preAnswerBufferRef.
        isStoppingRef.current = false;
      }

      if (!isActiveRef.current && !awaitingFinalizeRef.current && micEnabledRef.current) {
        // Start recognition directly — Speech Recognition handles its own mic
        // permission. The previous getUserMedia pre-check introduced a 0.5–2s async
        // delay that caused candidates' first words to be missed before capture started.
        // Skipped while awaitingFinalizeRef is true: the previous turn's
        // stop() hasn't resolved via onend yet, and starting again before
        // that settles risks an InvalidStateError.
        try {
          recognitionRef.current.start();
          console.log('✅ recognition.start() called successfully');
        } catch (e) {
          if (e.message && e.message.includes('already started')) {
            console.log('ℹ️ Already started (race condition), continuing...');
            isActiveRef.current = true;
          } else {
            console.error('❌ Failed to start recognition:', e);
          }
        }
      } else if (!micEnabledRef.current) {
        console.log('🔇 Mic muted — STT paused until unmute');
      } else {
        console.log('ℹ️ Recognition already active (or finalize in flight), not starting again');
      }

    } else if (wasRecordingTurn || interviewCompleted) {
      // ===== STOP: a real answer turn just ended (or the interview did) =====
      console.log('⏹️ Stopping recording...');

      isStoppingRef.current = true;

      if (silenceCheckRef.current) {
        clearInterval(silenceCheckRef.current);
        silenceCheckRef.current = null;
      }

      // Capture THEN close THEN submit: ask recognition to stop, but don't
      // read the transcript until it confirms via onend (which only fires
      // after any trailing final onresult has already landed). Reading it
      // synchronously right here — as this used to — could run before the
      // browser delivered the last word the candidate said, sending an
      // empty or truncated answer even though they clearly said something.
      if (isActiveRef.current) {
        awaitingFinalizeRef.current = true;
        if (finalizeTimeoutRef.current) clearTimeout(finalizeTimeoutRef.current);
        finalizeTimeoutRef.current = setTimeout(() => {
          if (awaitingFinalizeRef.current) {
            console.warn('⚠️ recognition.onend did not fire in time — finalizing with what we have');
            finalizeAndReportStop();
          }
        }, FINALIZE_TIMEOUT_MS);
        try {
          recognitionRef.current.stop();
          console.log('✅ recognition.stop() called, awaiting onend to finalize');
        } catch (e) {
          console.warn('⚠️ Error stopping recognition:', e);
          isActiveRef.current = false;
          finalizeAndReportStop(); // stop() itself failed — onend will never fire for this
        }
      } else {
        // Recognition wasn't running — nothing pending to wait for.
        finalizeAndReportStop();
      }

      // Stop MediaRecorder — onstop will emit the audio blob
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        try {
          mediaRecorderRef.current.stop();
          console.log('✅ MediaRecorder.stop() called');
        } catch (e) {
          console.warn('⚠️ MediaRecorder stop error:', e);
        }
        mediaRecorderRef.current = null;
      }
      whisperStreamRef.current = null;
      micPausedRef.current = false;

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    } else {
      // Neither an active turn nor AI speaking, and no turn to finalize
      // (e.g. a brief gap between the AI finishing and the next turn being
      // armed) — nothing to submit. Just make sure recognition isn't left
      // listening with no purpose.
      isStoppingRef.current = true;
      if (isActiveRef.current) {
        try { recognitionRef.current.stop(); } catch (e) { /* ignore */ }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecording, aiSpeaking, interviewCompleted]);

  // Soft-mute Whisper + STT without ending the answer session
  useEffect(() => {
    micEnabledRef.current = micEnabled;
    whisperStreamRef.current?.getAudioTracks?.().forEach((t) => {
      t.enabled = micEnabled;
    });

    if (!isRecording || interviewCompleted || !recognitionRef.current) {
      return undefined;
    }

    if (!micEnabled) {
      micPausedRef.current = true;
      if (isActiveRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          /* ignore */
        }
      }
      return undefined;
    }

    micPausedRef.current = false;
    if (!isActiveRef.current && !isStoppingRef.current) {
      try {
        recognitionRef.current.start();
      } catch (e) {
        if (!(e.message && e.message.includes('already started'))) {
          console.warn('⚠️ Unmute STT restart failed:', e.message);
        }
      }
    }
    return undefined;
  }, [micEnabled, isRecording, interviewCompleted]);

  // ============ RENDER ============
  return (
    <div className="voice-recorder" style={{ width: '100%' }}>
      {/* Recording Indicator - HIDDEN (timer is shown separately in parent component) */}
      {/* Removed to avoid duplication - timer is displayed in CandidateInterviewRoom */}
      
      {/* Live Transcript - Only show if showTranscript prop is true (for admin/debugging) */}
      {showTranscript && (finalTranscript || interimTranscript) && (
        <div style={{
          marginTop: '20px',
          padding: '20px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          minHeight: '100px',
          maxHeight: '300px',
          overflowY: 'auto',
          border: '2px solid #e9ecef'
        }}>
          <div style={{ 
            fontSize: '14px', 
            color: '#6c757d', 
            marginBottom: '10px',
            fontWeight: '600'
          }}>
            📝 Live Transcript:
          </div>
          <div style={{ fontSize: '16px', lineHeight: '1.8' }}>
            <span style={{ color: '#212529' }}>{finalTranscript}</span>
            <span style={{ color: '#adb5bd', fontStyle: 'italic' }}>{interimTranscript}</span>
          </div>
        </div>
      )}
      
      {/* Empty State — debug/admin view only (showTranscript). VoiceRecorder
          now stays mounted for the whole interview (continuous capture), so
          gating this on !isRecording alone would show it during every
          AI-speaking/idle moment in production; the parent already owns all
          real recording-state UI (recording-active-section). */}
      {showTranscript && !isRecording && !finalTranscript && !interimTranscript && (
        <div style={{
          padding: '40px 20px',
          textAlign: 'center',
          color: '#6c757d',
          fontSize: '15px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '2px dashed #dee2e6'
        }}>
          🎙️ Click "Start Answer" and speak your response
        </div>
      )}
    </div>
  );
};
